import { Download, Home, MoreVertical } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { usePwaInstall } from "@/AppContext";

function Step({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center text-foreground [&_svg]:size-8 [&_svg]:stroke-[2.25]"
        aria-hidden
      >
        {icon}
      </span>
      <p className="text-base font-semibold leading-snug text-foreground">{children}</p>
    </div>
  );
}

/** Android install UI. The deferred prompt is stored in context when `App` handles `beforeinstallprompt`. */
export function AndroidInstallInstructions({ appInstalled }: { appInstalled: boolean }) {
  const { deferredPrompt, promptInstall } = usePwaInstall();
  const [busy, setBusy] = useState(false);

  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) return;
    setBusy(true);
    try {
      await promptInstall();
    } finally {
      setBusy(false);
    }
  }, [deferredPrompt, promptInstall]);

  if (appInstalled) {
    return (
      <div className="mt-5 space-y-4">
        <Step icon={<Home />}>
          You&apos;re all set. Go to your home screen and tap this app&apos;s icon to open it.
        </Step>
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-4">
      {deferredPrompt ? (
        <div className="pb-1">
          <button
            type="button"
            disabled={busy}
            className="inline-flex h-11 w-full items-center justify-center rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            onClick={() => void handleInstallClick()}
          >
            {busy ? "Installing…" : "Add to Home Screen"}
          </button>
        </div>
      ) : (
        <p className="text-center text-sm text-muted-foreground">
          If your browser does not show an install button above, add the app from the menu:
        </p>
      )}

      {!deferredPrompt ? (
        <>
          <Step icon={<MoreVertical />}>Tap ⋮ (menu) in your browser</Step>
          <Step icon={<Download />}>Choose &quot;Install app&quot; or &quot;Add to Home screen&quot;</Step>
          <Step icon={<Home />}>Confirm to place the icon on your home screen</Step>
        </>
      ) : null}
    </div>
  );
}
